import { t, type TFunction } from '!/i18n'
import {
  ScheduleType,
  type Catchup,
  type CatchupProgram,
} from '@setplex/tria-api'
import { format, isToday } from 'date-fns'
import { type AdjustPurchaseableFn } from '~/features/payment/sse-purchases'
import { DATE_FORMATS, formatDateToLabelInfo } from '~/shared/helpers/date'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
} from '~/shared/interfaces'
import { type CatchupsRedirectParams } from '~/shared/interfaces'
import { getLabelPrice } from '~/shared/tools'

export const formatCatchupsDate = (startOfDayDate: number, t: TFunction) => {
  const formattedDate = format(startOfDayDate, DATE_FORMATS.DD_MMM)

  const date = isToday(startOfDayDate)
    ? t('today_date', {
        date: formattedDate,
      })
    : t('date-formatted', { lng: 'date', date: formattedDate })

  return { date, stamp: startOfDayDate }
}

export const formatPaidCatchup = (
  catchup: Catchup,
  adjust: AdjustPurchaseableFn
): CatchupFormatted => {
  const channel = adjust(catchup.channel)

  const { free, purchaseInfo, minCurrency, minPrice, name, channelNumber } =
    channel
  return {
    ...catchup,
    channel: {
      ...channel,
      formattedName: `${channelNumber}. ${name}`,
      labelPrice: getLabelPrice({
        free: Boolean(free),
        purchaseInfo,
        minCurrency,
        minPrice,
      }),
    },
  }
}

export const formatProgramsForSelectedDate = ({
  programs = [],
  catchup,
}: {
  programs?: CatchupProgram[]
  catchup: Catchup
}): CatchupProgramFormatted[] => {
  const formattedCardText = t('n_a')

  return programs
    .map((program) => {
      const timeline = formatDateToLabelInfo(program.startMs, program.endMs)
      return catchup?.scheduleType === ScheduleType.EPG
        ? {
            ...program,
            cardTitle: program.name,
            cardText: timeline,
          }
        : {
            ...program,
            cardTitle: timeline,
            cardText: formattedCardText,
          }
    })
    .sort((a, b) => b.startMs - a.startMs)
}

export const lockedFormat = ({
  allCatchups,
  redirect,
  unlockId,
  defaultStore,
}: {
  allCatchups: CatchupFormatted[]
  redirect: CatchupsRedirectParams
  unlockId: number
  defaultStore: Catchup[]
}): CatchupFormatted[] => {
  const channels = allCatchups.map((catchup, i) => {
    if (catchup.channel.id === redirect.channelId) {
      const locked = catchup.channel.id !== unlockId

      return {
        ...catchup,
        channel: {
          ...catchup.channel,
          locked,
        },
      }
    }
    return {
      ...catchup,
      channel: {
        ...catchup.channel,
        locked: defaultStore[i].channel.locked,
      },
    }
  })
  return channels
}
